import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ImageTools.css';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.1;
const DOUBLE_CLICK_ZOOM = 2;

const clampZoom = (value) => {
    const rounded = Math.round(value * 100) / 100;
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, rounded));
};

const ImageTools = ({
    imageRef,
    viewportRef,
    transformLayerRef,
    initialRotation = 0,
    onRotationChange,
    photoId,
}) => {
    const [isBarVisible, setIsBarVisible] = useState(false);
    const [hoveredTool, setHoveredTool] = useState(null);
    const [adjustments, setAdjustments] = useState({
        zoom: MIN_ZOOM,
        brightness: 0,
        contrast: 0,
        saturation: 0,
    });
    const [rotation, setRotation] = useState(initialRotation);
    const [isPanning, setIsPanning] = useState(false);

    const baseSizeRef = useRef(null);
    const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
    const scrollAdjustRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const toolRefs = {
        zoom: useRef(null),
        brightness: useRef(null),
        contrast: useRef(null),
        saturation: useRef(null),
        reset: useRef(null),
    };

    const measureBaseSize = useCallback(() => {
        const img = imageRef?.current;
        if (!img || img.offsetWidth === 0 || img.offsetHeight === 0) return;

        baseSizeRef.current = {
            width: img.offsetWidth,
            height: img.offsetHeight,
        };
    }, [imageRef]);

    const getViewportFitMaxHeight = useCallback(() => {
        const viewport = viewportRef?.current;
        if (!viewport) return '100%';

        if (viewport.clientHeight > 0) {
            return `${viewport.clientHeight}px`;
        }

        const viewportMax = getComputedStyle(viewport).maxHeight;
        if (viewportMax && viewportMax !== 'none') {
            return viewportMax;
        }

        return '100%';
    }, [viewportRef]);

    const resetViewportScroll = useCallback(() => {
        const viewport = viewportRef?.current;
        if (viewport) {
            viewport.scrollLeft = 0;
            viewport.scrollTop = 0;
        }
    }, [viewportRef]);

    const applyImageStyles = useCallback(() => {
        const img = imageRef?.current;
        const layer = transformLayerRef?.current;
        const viewport = viewportRef?.current;
        if (!img) return;

        const zoom = adjustments.zoom;
        const filters = [];

        if (adjustments.brightness !== 0) {
            filters.push(`brightness(${1 + adjustments.brightness / 100})`);
        }
        if (adjustments.contrast !== 0) {
            filters.push(`contrast(${1 + adjustments.contrast / 100})`);
        }
        if (adjustments.saturation !== 0) {
            filters.push(`saturate(${1 + adjustments.saturation / 100})`);
        }

        img.style.filter = filters.length > 0 ? filters.join(' ') : 'none';
        img.style.transform = `rotate(${rotation}deg)`;

        if (zoom <= MIN_ZOOM || !baseSizeRef.current) {
            const fitMaxHeight = getViewportFitMaxHeight();
            img.style.width = '100%';
            img.style.height = 'auto';
            img.style.maxWidth = '100%';
            img.style.maxHeight = fitMaxHeight;
            if (layer) {
                layer.style.width = '100%';
                layer.style.height = 'auto';
            }
            resetViewportScroll();
            requestAnimationFrame(() => measureBaseSize());
        } else {
            const { width: baseW, height: baseH } = baseSizeRef.current;
            const scaledW = baseW * zoom;
            const scaledH = baseH * zoom;

            img.style.width = `${scaledW}px`;
            img.style.height = `${scaledH}px`;
            img.style.maxWidth = 'none';
            img.style.maxHeight = 'none';

            if (layer) {
                layer.style.width = `${scaledW}px`;
                layer.style.height = `${scaledH}px`;
            }
        }

        if (viewport) {
            viewport.classList.toggle('photo-modal-viewport--zoomed', zoom > MIN_ZOOM);
        }
    }, [adjustments, rotation, imageRef, transformLayerRef, viewportRef, resetViewportScroll, measureBaseSize, getViewportFitMaxHeight]);

    const applyScrollAdjust = useCallback(() => {
        const viewport = viewportRef?.current;
        const adjust = scrollAdjustRef.current;
        if (!viewport || !adjust) return;

        const { ratioX, ratioY, pointerX, pointerY } = adjust;
        const { width: baseW, height: baseH } = baseSizeRef.current || {};
        if (!baseW || !baseH) return;

        const newLayerW = baseW * adjustments.zoom;
        const newLayerH = baseH * adjustments.zoom;
        const newPointerX = ratioX * newLayerW;
        const newPointerY = ratioY * newLayerH;

        viewport.scrollLeft += newPointerX - pointerX;
        viewport.scrollTop += newPointerY - pointerY;
        scrollAdjustRef.current = null;
    }, [viewportRef, adjustments.zoom]);

    const setZoomLevel = useCallback((nextZoom, scrollAdjust = null) => {
        const clamped = clampZoom(nextZoom);
        scrollAdjustRef.current = scrollAdjust;
        setAdjustments((prev) => {
            if (prev.zoom === clamped) return prev;
            return { ...prev, zoom: clamped };
        });
    }, []);

    useEffect(() => {
        setAdjustments({
            zoom: MIN_ZOOM,
            brightness: 0,
            contrast: 0,
            saturation: 0,
        });
        setRotation(initialRotation);
        setHoveredTool(null);
        baseSizeRef.current = null;
        resetViewportScroll();
    }, [photoId, initialRotation, resetViewportScroll]);

    useEffect(() => {
        applyImageStyles();
        applyScrollAdjust();
    }, [adjustments.zoom, rotation, adjustments.brightness, adjustments.contrast, adjustments.saturation, applyImageStyles, applyScrollAdjust]);

    useEffect(() => {
        const img = imageRef?.current;
        if (!img) return undefined;

        const handleLoad = () => {
            if (adjustments.zoom <= MIN_ZOOM) {
                measureBaseSize();
            }
            applyImageStyles();
        };

        if (img.complete) {
            handleLoad();
        }

        img.addEventListener('load', handleLoad);
        return () => img.removeEventListener('load', handleLoad);
    }, [photoId, imageRef, measureBaseSize, applyImageStyles, adjustments.zoom]);

    useEffect(() => {
        const viewport = viewportRef?.current;
        if (!viewport || typeof ResizeObserver === 'undefined') return undefined;

        const handleResize = () => {
            if (adjustments.zoom <= MIN_ZOOM) {
                baseSizeRef.current = null;
                measureBaseSize();
            }
            applyImageStyles();
        };

        const observer = new ResizeObserver(handleResize);
        observer.observe(viewport);
        return () => observer.disconnect();
    }, [viewportRef, adjustments.zoom, measureBaseSize, applyImageStyles]);

    const handleWheel = useCallback((e) => {
        const viewport = viewportRef?.current;
        const layer = transformLayerRef?.current;
        if (!viewport || !layer) return;

        e.preventDefault();
        e.stopPropagation();

        if (!baseSizeRef.current) {
            measureBaseSize();
        }
        if (!baseSizeRef.current) return;

        const direction = e.deltaY > 0 ? -1 : 1;
        const nextZoom = clampZoom(adjustments.zoom + direction * ZOOM_STEP);
        if (nextZoom === adjustments.zoom) return;

        const layerRect = layer.getBoundingClientRect();
        const pointerX = e.clientX - layerRect.left;
        const pointerY = e.clientY - layerRect.top;
        const ratioX = layerRect.width > 0 ? pointerX / layerRect.width : 0.5;
        const ratioY = layerRect.height > 0 ? pointerY / layerRect.height : 0.5;

        setZoomLevel(nextZoom, { ratioX, ratioY, pointerX, pointerY });
    }, [adjustments.zoom, viewportRef, transformLayerRef, measureBaseSize, setZoomLevel]);

    const handleDoubleClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        const nextZoom = adjustments.zoom === MIN_ZOOM ? DOUBLE_CLICK_ZOOM : MIN_ZOOM;
        setZoomLevel(nextZoom);
    }, [adjustments.zoom, setZoomLevel]);

    const handleMouseDown = useCallback((e) => {
        if (adjustments.zoom <= MIN_ZOOM) return;
        const viewport = viewportRef?.current;
        if (!viewport) return;

        setIsPanning(true);
        viewport.classList.add('photo-modal-viewport--panning');
        panStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: viewport.scrollLeft,
            scrollTop: viewport.scrollTop,
        };
        e.preventDefault();
    }, [adjustments.zoom, viewportRef]);

    const handleMouseMove = useCallback((e) => {
        if (!isPanning) return;
        const viewport = viewportRef?.current;
        if (!viewport) return;

        const { x, y, scrollLeft, scrollTop } = panStartRef.current;
        viewport.scrollLeft = scrollLeft - (e.clientX - x);
        viewport.scrollTop = scrollTop - (e.clientY - y);
        e.preventDefault();
    }, [isPanning, viewportRef]);

    const handleMouseUp = useCallback(() => {
        setIsPanning(false);
        viewportRef?.current?.classList.remove('photo-modal-viewport--panning');
    }, [viewportRef]);

    useEffect(() => {
        const viewport = viewportRef?.current;
        if (!viewport) return undefined;

        viewport.addEventListener('wheel', handleWheel, { passive: false });
        viewport.addEventListener('mousedown', handleMouseDown);
        viewport.addEventListener('dblclick', handleDoubleClick);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            viewport.removeEventListener('wheel', handleWheel);
            viewport.removeEventListener('mousedown', handleMouseDown);
            viewport.removeEventListener('dblclick', handleDoubleClick);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [viewportRef, handleWheel, handleMouseDown, handleDoubleClick, handleMouseMove, handleMouseUp]);

    const handleSliderChange = useCallback((property, value) => {
        if (property === 'zoom') {
            if (!baseSizeRef.current) {
                measureBaseSize();
            }
            setZoomLevel(parseFloat(value));
            return;
        }

        setAdjustments((prev) => ({
            ...prev,
            [property]: parseFloat(value),
        }));
    }, [measureBaseSize, setZoomLevel]);

    const handleRotation = useCallback(() => {
        const newRotation = rotation + 90;
        setRotation(newRotation);
        resetViewportScroll();
        if (onRotationChange) {
            onRotationChange(newRotation);
        }
    }, [rotation, onRotationChange, resetViewportScroll]);

    const resetAdjustments = useCallback(() => {
        setAdjustments({
            zoom: MIN_ZOOM,
            brightness: 0,
            contrast: 0,
            saturation: 0,
        });
        resetViewportScroll();
    }, [resetViewportScroll]);

    const handleToolEnter = useCallback((toolName) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setHoveredTool(toolName);
    }, []);

    const handleToolLeave = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredTool(null);
        }, 300);
    }, []);

    useEffect(() => () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
    }, []);

    const zoomPercent = Math.round(adjustments.zoom * 100);

    return (
        <>
            {adjustments.zoom > MIN_ZOOM && (
                <div className="image-tools-zoom-indicator" aria-live="polite">
                    {zoomPercent}%
                </div>
            )}

            {!isBarVisible ? (
                <button
                    type="button"
                    className="image-tools-toggle-button"
                    onClick={() => setIsBarVisible(true)}
                    title="Mostrar herramientas"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" />
                    </svg>
                </button>
            ) : (
                <div className="image-tools-sidebar">
                    <button
                        type="button"
                        className="image-tools-hide-button"
                        onClick={() => setIsBarVisible(false)}
                        title="Ocultar herramientas"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>

                    <div
                        className="image-tool-item"
                        onMouseEnter={() => handleToolEnter('zoom')}
                        onMouseLeave={handleToolLeave}
                        ref={toolRefs.zoom}
                    >
                        <button type="button" className="image-tool-icon" title="Zoom">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                        </button>
                        {hoveredTool === 'zoom' && (
                            <div className="image-tool-panel">
                                <div className="tool-panel-content">
                                    <label className="tool-panel-label">Zoom</label>
                                    <input
                                        type="range"
                                        min={MIN_ZOOM}
                                        max={MAX_ZOOM}
                                        step={ZOOM_STEP}
                                        value={adjustments.zoom}
                                        onChange={(e) => handleSliderChange('zoom', e.target.value)}
                                        className="tool-panel-slider"
                                    />
                                    <span className="tool-panel-value">{zoomPercent}%</span>
                                    <span className="tool-panel-hint">Rueda del mouse o doble clic</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        className="image-tool-item"
                        onMouseEnter={() => handleToolEnter('brightness')}
                        onMouseLeave={handleToolLeave}
                        ref={toolRefs.brightness}
                    >
                        <button type="button" className="image-tool-icon" title="Brillo">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="5" />
                                <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72 1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                            </svg>
                        </button>
                        {hoveredTool === 'brightness' && (
                            <div className="image-tool-panel">
                                <div className="tool-panel-content">
                                    <label className="tool-panel-label">Brillo</label>
                                    <input
                                        type="range"
                                        min="-100"
                                        max="100"
                                        step="1"
                                        value={adjustments.brightness}
                                        onChange={(e) => handleSliderChange('brightness', e.target.value)}
                                        className="tool-panel-slider"
                                    />
                                    <span className="tool-panel-value">{adjustments.brightness}%</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        className="image-tool-item"
                        onMouseEnter={() => handleToolEnter('contrast')}
                        onMouseLeave={handleToolLeave}
                        ref={toolRefs.contrast}
                    >
                        <button type="button" className="image-tool-icon" title="Contraste">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2v20M2 12h20" />
                            </svg>
                        </button>
                        {hoveredTool === 'contrast' && (
                            <div className="image-tool-panel">
                                <div className="tool-panel-content">
                                    <label className="tool-panel-label">Contraste</label>
                                    <input
                                        type="range"
                                        min="-100"
                                        max="100"
                                        step="1"
                                        value={adjustments.contrast}
                                        onChange={(e) => handleSliderChange('contrast', e.target.value)}
                                        className="tool-panel-slider"
                                    />
                                    <span className="tool-panel-value">{adjustments.contrast}%</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        className="image-tool-item"
                        onMouseEnter={() => handleToolEnter('saturation')}
                        onMouseLeave={handleToolLeave}
                        ref={toolRefs.saturation}
                    >
                        <button type="button" className="image-tool-icon" title="Color">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                        </button>
                        {hoveredTool === 'saturation' && (
                            <div className="image-tool-panel">
                                <div className="tool-panel-content">
                                    <label className="tool-panel-label">Color</label>
                                    <input
                                        type="range"
                                        min="-100"
                                        max="100"
                                        step="1"
                                        value={adjustments.saturation}
                                        onChange={(e) => handleSliderChange('saturation', e.target.value)}
                                        className="tool-panel-slider"
                                    />
                                    <span className="tool-panel-value">{adjustments.saturation}%</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="image-tool-item">
                        <button
                            type="button"
                            className="image-tool-icon"
                            onClick={handleRotation}
                            title="Rotar 90° (sentido horario)"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                <path d="M3 21v-5h5" />
                            </svg>
                        </button>
                    </div>

                    <div
                        className="image-tool-item"
                        onMouseEnter={() => handleToolEnter('reset')}
                        onMouseLeave={handleToolLeave}
                        ref={toolRefs.reset}
                    >
                        <button
                            type="button"
                            className="image-tool-icon image-tool-icon-reset"
                            onClick={resetAdjustments}
                            title="Restablecer ajustes"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                <path d="M3 21v-5h5" />
                            </svg>
                        </button>
                        {hoveredTool === 'reset' && (
                            <div className="image-tool-panel">
                                <div className="tool-panel-content">
                                    <span className="tool-panel-hint">Restablecer todos los ajustes</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ImageTools;
